import { Phone, PhoneOff, Video } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Avatar } from '@/components/ui/Avatar'
import type { ActiveCall } from '../types'

/** A call is announced for this long after it starts; after that it is only shown in the conversation. */
const RING_SECONDS = 45

interface IncomingCallsProps {
  calls: ActiveCall[]
  activeCallId: number | null
  onJoin: (call: ActiveCall, video: boolean) => void
}

/** Cards for calls someone just started in one of my conversations, with a soft ring. */
export function IncomingCalls({ calls, activeCallId, onJoin }: IncomingCallsProps) {
  const { t } = useTranslation('messaging')
  const [dismissed, setDismissed] = useState<number[]>([])
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 5000)
    return () => window.clearInterval(timer)
  }, [])

  const ringing = calls.filter(
    (c) =>
      !c.mine &&
      c.ongoing &&
      c.id !== activeCallId &&
      !dismissed.includes(c.id) &&
      now - new Date(c.startedAt).getTime() < RING_SECONDS * 1000,
  )
  useRing(ringing.length > 0 && activeCallId === null)

  if (ringing.length === 0) return null
  return (
    <div
      className="fixed right-4 bottom-4 z-[66] flex w-[min(22rem,calc(100vw-2rem))] flex-col gap-3"
      aria-live="assertive"
    >
      {ringing.map((call) => {
        const classRoom = call.conversation.kind === 'class'
        const who = call.startedBy?.name ?? call.conversation.title
        return (
          <div
            key={call.id}
            className="animate-slide-up rounded-panel border border-line bg-surface p-4 shadow-overlay"
          >
            <div className="flex items-center gap-3">
              <Avatar
                name={who}
                src={call.startedBy?.avatarUrl}
                size="md"
                className="ring-4 ring-success-100"
              />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-ink">
                  {classRoom ? t('Live class: {{title}}', { title: call.conversation.title }) : who}
                </p>
                <p className="truncate text-xs text-muted">
                  {classRoom
                    ? t('Started by {{name}}', { name: who })
                    : call.video
                      ? t('Video call')
                      : t('Audio call')}
                  {call.conversation.kind === 'group' ? ` · ${call.conversation.title}` : ''}
                </p>
              </div>
            </div>
            <div className="mt-4 flex gap-2">
              <button
                type="button"
                onClick={() => setDismissed((d) => [...d, call.id])}
                className="inline-flex flex-1 items-center justify-center gap-2 rounded-control border border-line px-3 py-2 text-sm font-medium text-ink-soft hover:bg-surface-muted"
              >
                <PhoneOff className="size-4" aria-hidden="true" />
                {t('Not now')}
              </button>
              <button
                type="button"
                onClick={() => onJoin(call, false)}
                className="inline-flex items-center justify-center gap-2 rounded-control bg-success-600 px-3 py-2 text-sm font-semibold text-white hover:bg-success-700"
                aria-label={t('Join with audio only')}
                title={t('Join with audio only')}
              >
                <Phone className="size-4" aria-hidden="true" />
              </button>
              {call.video ? (
                <button
                  type="button"
                  onClick={() => onJoin(call, true)}
                  className="inline-flex items-center justify-center gap-2 rounded-control bg-success-600 px-3 py-2 text-sm font-semibold text-white hover:bg-success-700"
                >
                  <Video className="size-4" aria-hidden="true" />
                  {t('Join')}
                </button>
              ) : null}
            </div>
          </div>
        )
      })}
    </div>
  )
}

/** Two short tones every two seconds while a call is announced (silently ignored if the browser blocks audio). */
function useRing(on: boolean): void {
  useEffect(() => {
    if (!on || typeof window.AudioContext === 'undefined') return
    let created: AudioContext
    try {
      created = new AudioContext()
    } catch {
      return
    }
    const ctx = created
    const beep = (at: number) => {
      const osc = ctx.createOscillator()
      const gain = ctx.createGain()
      osc.frequency.value = 660
      gain.gain.setValueAtTime(0.0001, at)
      gain.gain.exponentialRampToValueAtTime(0.08, at + 0.02)
      gain.gain.exponentialRampToValueAtTime(0.0001, at + 0.35)
      osc.connect(gain).connect(ctx.destination)
      osc.start(at)
      osc.stop(at + 0.4)
    }
    const ring = () => {
      if (ctx.state === 'closed') return
      const t0 = ctx.currentTime
      beep(t0)
      beep(t0 + 0.45)
    }
    ring()
    const timer = window.setInterval(ring, 2000)
    return () => {
      window.clearInterval(timer)
      void ctx.close().catch(() => undefined)
    }
  }, [on])
}
